/** Nagłówki podstron. Renderuje je wyłącznie Topbar — widoki nie powielają tytułu. */

export type PageHeaderConfig = {
  titleKey: string
  descriptionKey?: string
}

export const pageHeaders: Record<string, PageHeaderConfig> = {
  // `/` renderuje HomeRedirect → Dashboard, więc ma ten sam nagłówek
  '/': { titleKey: 'headers.dashboard.title', descriptionKey: 'headers.dashboard.description' },
  '/dashboard': { titleKey: 'headers.dashboard.title', descriptionKey: 'headers.dashboard.description' },

  // Serwis
  '/service': { titleKey: 'headers.service.title', descriptionKey: 'headers.service.description' },
  '/service/requests': {
    titleKey: 'headers.serviceRequests.title',
    descriptionKey: 'headers.serviceRequests.description',
  },
  '/service/orders/new': {
    titleKey: 'headers.serviceOrderNew.title',
    descriptionKey: 'headers.serviceOrderNew.description',
  },
  '/service/archive': {
    titleKey: 'headers.serviceArchive.title',
    descriptionKey: 'headers.serviceArchive.description',
  },
  '/service/activities': {
    titleKey: 'headers.serviceActivities.title',
    descriptionKey: 'headers.serviceActivities.description',
  },
  '/service/activities/new': {
    titleKey: 'headers.serviceActivityNew.title',
    descriptionKey: 'headers.serviceActivityNew.description',
  },

  // Moje zlecenia (serwisant)
  '/my-orders': { titleKey: 'headers.myOrders.title', descriptionKey: 'headers.myOrders.description' },

  // Administracja
  '/admin': { titleKey: 'admin.headers.root.title', descriptionKey: 'admin.headers.root.description' },
  '/admin/users': { titleKey: 'admin.headers.users.title', descriptionKey: 'admin.headers.users.description' },
  '/admin/users/new': {
    titleKey: 'admin.headers.usersNew.title',
    descriptionKey: 'admin.headers.usersNew.description',
  },
  '/admin/firms': { titleKey: 'admin.headers.firms.title', descriptionKey: 'admin.headers.firms.description' },
  '/admin/firms/new': {
    titleKey: 'admin.headers.firmsNew.title',
    descriptionKey: 'admin.headers.firmsNew.description',
  },
  '/admin/devices': { titleKey: 'admin.headers.devices.title', descriptionKey: 'admin.headers.devices.description' },
  '/admin/devices/new': {
    titleKey: 'admin.headers.devicesNew.title',
    descriptionKey: 'admin.headers.devicesNew.description',
  },
  '/admin/inverters-and-alarms': {
    titleKey: 'admin.headers.invertersAndAlarms.title',
    descriptionKey: 'admin.headers.invertersAndAlarms.description',
  },
  '/admin/inverters-and-alarms/inverters': {
    titleKey: 'admin.headers.inverters.title',
    descriptionKey: 'admin.headers.inverters.description',
  },
  '/admin/inverters-and-alarms/inverters/new': {
    titleKey: 'admin.headers.invertersNew.title',
    descriptionKey: 'admin.headers.invertersNew.description',
  },
  '/admin/inverters-and-alarms/alarms': {
    titleKey: 'admin.headers.alarms.title',
    descriptionKey: 'admin.headers.alarms.description',
  },
  '/admin/inverters-and-alarms/alarms/new': {
    titleKey: 'admin.headers.alarmsNew.title',
    descriptionKey: 'admin.headers.alarmsNew.description',
  },
  '/admin/notifications': {
    titleKey: 'admin.headers.notifications.title',
    descriptionKey: 'admin.headers.notifications.description',
  },
  '/admin/notifications/list': {
    titleKey: 'admin.headers.notificationsList.title',
    descriptionKey: 'admin.headers.notificationsList.description',
  },
  '/admin/notifications/list/new': {
    titleKey: 'admin.headers.notificationsListNew.title',
    descriptionKey: 'admin.headers.notificationsListNew.description',
  },
  '/admin/notifications/templates': {
    titleKey: 'admin.headers.notificationTemplates.title',
    descriptionKey: 'admin.headers.notificationTemplates.description',
  },
  '/admin/notifications/templates/new': {
    titleKey: 'admin.headers.notificationTemplatesNew.title',
    descriptionKey: 'admin.headers.notificationTemplatesNew.description',
  },
  '/admin/notifications/senders': {
    titleKey: 'admin.headers.notificationSenders.title',
    descriptionKey: 'admin.headers.notificationSenders.description',
  },
  '/admin/notifications/senders/new': {
    titleKey: 'admin.headers.notificationSendersNew.title',
    descriptionKey: 'admin.headers.notificationSendersNew.description',
  },
  '/admin/notifications/recipients': {
    titleKey: 'admin.headers.notificationRecipients.title',
    descriptionKey: 'admin.headers.notificationRecipients.description',
  },
  '/admin/notifications/recipients/new': {
    titleKey: 'admin.headers.notificationRecipientsNew.title',
    descriptionKey: 'admin.headers.notificationRecipientsNew.description',
  },
  '/admin/wizard': { titleKey: 'admin.headers.wizard.title', descriptionKey: 'admin.headers.wizard.description' },

  // RODO / Dziennik zdarzeń
  '/rodo': { titleKey: 'headers.rodo.title', descriptionKey: 'headers.rodo.description' },
  '/rodo/users-consents': {
    titleKey: 'headers.rodoConsents.title',
    descriptionKey: 'headers.rodoConsents.description',
  },
  '/rodo/privacy-policies': {
    titleKey: 'headers.rodoPrivacyPolicies.title',
    descriptionKey: 'headers.rodoPrivacyPolicies.description',
  },
  '/rodo/data-processing-consents': {
    titleKey: 'headers.rodoDataProcessingConsents.title',
    descriptionKey: 'headers.rodoDataProcessingConsents.description',
  },
  '/audit-log': { titleKey: 'headers.auditLog.title', descriptionKey: 'headers.auditLog.description' },

  // Konto użytkownika
  '/profile': { titleKey: 'headers.profile.title', descriptionKey: 'headers.profile.description' },
  '/profile/settings': {
    titleKey: 'headers.profileSettings.title',
    descriptionKey: 'headers.profileSettings.description',
  },
  '/profile/documents': {
    titleKey: 'headers.profileDocuments.title',
    descriptionKey: 'headers.profileDocuments.description',
  },
}

const dynamicHeaders: { match: RegExp; header: PageHeaderConfig }[] = [
  // Panel urządzenia
  {
    match: /^\/device\/[^/]+\/alarms$/,
    header: { titleKey: 'headers.deviceAlarms.title', descriptionKey: 'headers.deviceAlarms.description' },
  },
  {
    match: /^\/device\/[^/]+\/technical-inspection-history$/,
    header: { titleKey: 'headers.deviceInspections.title', descriptionKey: 'headers.deviceInspections.description' },
  },
  {
    match: /^\/device\/[^/]+\/technical-inspection$/,
    header: { titleKey: 'headers.deviceInspections.title', descriptionKey: 'headers.deviceInspections.description' },
  },
  {
    match: /^\/device\/[^/]+\/help$/,
    header: { titleKey: 'headers.deviceHelp.title', descriptionKey: 'headers.deviceHelp.description' },
  },
  {
    match: /^\/device\/[^/]+$/,
    header: { titleKey: 'headers.devicePanel.title', descriptionKey: 'headers.devicePanel.description' },
  },

  // Serwis — deep-linki i formularz realizacji
  {
    match: /^\/service\/requests\/[^/]+$/,
    header: { titleKey: 'headers.serviceRequests.title', descriptionKey: 'headers.serviceRequestDetails.description' },
  },
  {
    match: /^\/service\/orders\/[^/]+\/execute$/,
    header: { titleKey: 'headers.serviceExecution.title', descriptionKey: 'headers.serviceExecution.description' },
  },
  {
    match: /^\/my-orders\/[^/]+$/,
    header: { titleKey: 'headers.serviceExecution.title', descriptionKey: 'headers.serviceExecution.description' },
  },
  {
    match: /^\/my-orders\/[^/]+\/related\/[^/]+$/,
    header: {
      titleKey: 'headers.serviceRelatedOrder.title',
      descriptionKey: 'headers.serviceRelatedOrder.description',
    },
  },
  {
    match: /^\/service\/archive\/[^/]+$/,
    header: { titleKey: 'headers.serviceExecution.title', descriptionKey: 'headers.serviceArchiveDetails.description' },
  },
  {
    match: /^\/service\/orders\/[^/]+\/edit$/,
    header: { titleKey: 'headers.serviceOrderEdit.title', descriptionKey: 'headers.serviceOrderEdit.description' },
  },
  {
    match: /^\/service\/orders\/[^/]+$/,
    header: {
      titleKey: 'headers.serviceOrderDetails.title',
      descriptionKey: 'headers.serviceOrderDetails.description',
    },
  },
  {
    match: /^\/service\/activities\/[^/]+\/edit$/,
    header: {
      titleKey: 'headers.serviceActivityEdit.title',
      descriptionKey: 'headers.serviceActivityEdit.description',
    },
  },
  {
    match: /^\/service\/activities\/[^/]+$/,
    header: {
      titleKey: 'headers.serviceActivityDetails.title',
      descriptionKey: 'headers.serviceActivityDetails.description',
    },
  },

  // Administracja — szczegóły i edycja
  {
    match: /^\/admin\/users\/[^/]+\/edit$/,
    header: { titleKey: 'admin.headers.usersEdit.title', descriptionKey: 'admin.headers.usersEdit.description' },
  },
  {
    match: /^\/admin\/users\/[^/]+$/,
    header: { titleKey: 'admin.headers.usersDetails.title', descriptionKey: 'admin.headers.usersDetails.description' },
  },
  {
    match: /^\/admin\/firms\/[^/]+\/edit$/,
    header: { titleKey: 'admin.headers.firmsEdit.title', descriptionKey: 'admin.headers.firmsEdit.description' },
  },
  {
    match: /^\/admin\/firms\/[^/]+$/,
    header: { titleKey: 'admin.headers.firmsDetails.title', descriptionKey: 'admin.headers.firmsDetails.description' },
  },
  {
    match: /^\/admin\/devices\/[^/]+\/edit$/,
    header: { titleKey: 'admin.headers.devicesEdit.title', descriptionKey: 'admin.headers.devicesEdit.description' },
  },
  {
    match: /^\/admin\/devices\/[^/]+$/,
    header: {
      titleKey: 'admin.headers.devicesDetails.title',
      descriptionKey: 'admin.headers.devicesDetails.description',
    },
  },
  {
    match: /^\/admin\/inverters-and-alarms\/inverters\/[^/]+\/edit$/,
    header: {
      titleKey: 'admin.headers.invertersEdit.title',
      descriptionKey: 'admin.headers.invertersEdit.description',
    },
  },
  {
    match: /^\/admin\/inverters-and-alarms\/inverters\/[^/]+$/,
    header: {
      titleKey: 'admin.headers.invertersDetails.title',
      descriptionKey: 'admin.headers.invertersDetails.description',
    },
  },
  {
    match: /^\/admin\/inverters-and-alarms\/alarms\/[^/]+\/edit$/,
    header: { titleKey: 'admin.headers.alarmsEdit.title', descriptionKey: 'admin.headers.alarmsEdit.description' },
  },
  {
    match: /^\/admin\/inverters-and-alarms\/alarms\/[^/]+$/,
    header: {
      titleKey: 'admin.headers.alarmsDetails.title',
      descriptionKey: 'admin.headers.alarmsDetails.description',
    },
  },
  {
    match: /^\/admin\/notifications\/list\/[^/]+\/edit$/,
    header: {
      titleKey: 'admin.headers.notificationsListEdit.title',
      descriptionKey: 'admin.headers.notificationsListEdit.description',
    },
  },
  {
    match: /^\/admin\/notifications\/list\/[^/]+$/,
    header: {
      titleKey: 'admin.headers.notificationsListDetails.title',
      descriptionKey: 'admin.headers.notificationsListDetails.description',
    },
  },
  {
    match: /^\/admin\/notifications\/templates\/[^/]+\/edit$/,
    header: {
      titleKey: 'admin.headers.notificationTemplatesEdit.title',
      descriptionKey: 'admin.headers.notificationTemplatesEdit.description',
    },
  },
  {
    match: /^\/admin\/notifications\/templates\/[^/]+$/,
    header: {
      titleKey: 'admin.headers.notificationTemplatesDetails.title',
      descriptionKey: 'admin.headers.notificationTemplatesDetails.description',
    },
  },
  {
    match: /^\/admin\/notifications\/senders\/[^/]+\/edit$/,
    header: {
      titleKey: 'admin.headers.notificationSendersEdit.title',
      descriptionKey: 'admin.headers.notificationSendersEdit.description',
    },
  },
  {
    match: /^\/admin\/notifications\/senders\/[^/]+$/,
    header: {
      titleKey: 'admin.headers.notificationSendersDetails.title',
      descriptionKey: 'admin.headers.notificationSendersDetails.description',
    },
  },
  {
    match: /^\/admin\/notifications\/recipients\/[^/]+\/edit$/,
    header: {
      titleKey: 'admin.headers.notificationRecipientsEdit.title',
      descriptionKey: 'admin.headers.notificationRecipientsEdit.description',
    },
  },
  {
    match: /^\/admin\/notifications\/recipients\/[^/]+$/,
    header: {
      titleKey: 'admin.headers.notificationRecipientsDetails.title',
      descriptionKey: 'admin.headers.notificationRecipientsDetails.description',
    },
  },
]

/** Nagłówek dla bieżącej trasy (null → brak nagłówka, np. 404). */
export function headerForPath(pathname: string): PageHeaderConfig | null {
  if (pageHeaders[pathname]) return pageHeaders[pathname]

  const dynamic = dynamicHeaders.find((entry) => entry.match.test(pathname))

  return dynamic ? dynamic.header : null
}
